// What still stands between the guest and "Add to visit" in the option-led
// availability panel (Booking V3), in the order the guest meets it: what →
// how many → when. The owner, circling a greyed "Add to visit →" beside
// "Choose an option" (25 Sept): "Add a hint about what is outstanding so
// guests know what to do".
//
// One answer drives three things:
//  * the next-step line in the visit summary and the phone bar ("Next: pick
//    a start time for Sat 26 Sept"), worded from what is actually missing;
//  * where a press on the not-yet-ready Add takes the guest (`step` and
//    `target`: the panel scrolls there, focuses it and highlights it — the
//    button is never a dead end, and it never adds anything until ready);
//  * the step headings: a done tick on each finished step, and the one that
//    is next marked as next.
//
// Pure: no DOM, no React, no runtime imports beyond together-logic (itself
// pure), so e2e/next-step-logic.spec.ts runs it under Node.

import type { DayStatus } from "./availability-logic";
import { togetherVerb } from "./together-logic";

type Noun = { one: string; other: string };

/** The panel's three steps; "What" and "How many" share one heading. */
export type StepId = "what" | "howMany" | "when";
export type StepState = "done" | "next" | "todo";

/**
 * What a press on the not-ready Add focuses (the panel maps it to the DOM):
 * the first option card, the stepper of the option whose count is off, the
 * enquiry button, the day strip, the chosen day's first start, or the first
 * "ride together" / "keep" button.
 */
export type NextTarget = "options" | "party" | "enquire" | "days" | "starts" | "together";

export type NextStepEntry = {
  unitId: string;
  name: string;
  /** Guests on this option as chosen. */
  guests: number;
  /** The fewest one booking of it takes (1: no floor). */
  min: number;
  /** The most it takes here (null: no stated ceiling). */
  max: number | null;
};

export type NextStepInput = {
  shape: "sessions" | "multi";
  noun: Noun;
  /** What is chosen, one per option (a session is one entry). */
  entries: readonly NextStepEntry[];
  /** The most options one visit can carry (MAX_ENTRIES). */
  maxEntries: number;
  /** The visit is over a "block" party limit: blockCap is that limit. */
  overBlock: boolean;
  blockCap: number | null;
  /** The visit is over the size the venue takes online (by enquiry above). */
  oversize: boolean;
  /** The panel offers an enquiry for it. */
  canEnquire: boolean;
  /** The chosen day's short label ("Sat 26 Sept"); null: no day yet. */
  dayShort: string | null;
  /** The day's answer is still on its way. */
  checking: boolean;
  /** The chosen day offers start times (free, or unchecked times after a failed read). */
  startsOffered: boolean;
  /** The chosen options can't go out at the same time that day, and the nudge offers a way: together or apart. */
  together: "together" | "apart" | null;
  /** The day's own words when it offers nothing (closed, too soon, full). */
  dayMessage: string | null;
  /** The chosen start, as the summary shows it ("08:30"); null: none yet. */
  startHm: string | null;
  /** Everything chosen is already in the visit at that start. */
  alreadyIn: boolean;
};

export type NextStep = {
  /** Add holds now. */
  ready: boolean;
  /** The line the summary shows while Add is not ready; null when ready. */
  text: string | null;
  /**
   * Whether the line is worth announcing (aria-live). "Checking
   * availability…" is not: the panel's own live region says that already.
   */
  announce: boolean;
  /** The step the guest acts on next; null when ready. */
  step: StepId | null;
  target: NextTarget | null;
  /** For target "party": the option whose count needs changing, and which way. */
  unitId: string | null;
  adjust: "more" | "fewer" | null;
  /** Each heading's mark: done tick, next, or neither. */
  steps: Record<StepId, StepState>;
};

/** What the service calls one of its options: a ride (riders), a session, or an option. */
export function optionWord(shape: "sessions" | "multi", noun: Noun): { one: string; other: string; a: string } {
  if (togetherVerb(noun) === "ride") return { one: "ride", other: "rides", a: "a ride" };
  if (shape === "sessions") return { one: "session", other: "sessions", a: "a session" };
  return { one: "option", other: "options", a: "an option" };
}

/** "2–4", "2 or more", or "2": how many one booking of an option takes. */
export function takesText(min: number, max: number | null): string {
  if (max == null) return `${min} or more`;
  return max <= min ? `${min}` : `${min}–${max}`;
}

export function nextStep(i: NextStepInput): NextStep {
  const word = optionWord(i.shape, i.noun);
  const whatDone = i.entries.length > 0 && i.entries.length <= i.maxEntries;
  const short = i.entries.find((e) => e.guests < e.min) ?? null;
  const howManyDone = whatDone && !short && !i.overBlock && !i.oversize;
  const out = (
    step: StepId,
    text: string,
    target: NextTarget,
    party: { unitId: string; adjust: "more" | "fewer" } | null = null,
    announce = true,
  ): NextStep => ({
    ready: false,
    text,
    announce,
    step,
    target,
    unitId: party?.unitId ?? null,
    adjust: party?.adjust ?? null,
    steps: {
      what: step === "what" ? "next" : whatDone ? "done" : "todo",
      howMany: step === "howMany" ? "next" : howManyDone ? "done" : "todo",
      when: step === "when" ? "next" : "todo",
    },
  });

  // 1. What
  if (i.entries.length === 0) return out("what", `Next: choose ${word.a} above`, "options");
  if (i.entries.length > i.maxEntries)
    return out("what", `Next: choose up to ${i.maxEntries} ${word.other} for one visit`, "options");

  // 2. How many
  if (short)
    return out("howMany", `Next: add ${i.noun.other} — ${short.name} takes ${takesText(short.min, short.max)}`, "party", {
      unitId: short.unitId,
      adjust: "more",
    });
  // Fewer: on the option with the most guests.
  const fewer = { unitId: [...i.entries].sort((a, b) => b.guests - a.guests)[0]!.unitId, adjust: "fewer" as const };
  if (i.overBlock && i.blockCap != null)
    return out("howMany", `Next: choose fewer ${i.noun.other} — up to ${i.blockCap} in one visit`, "party", fewer);
  if (i.oversize) {
    // The enquiry button sits in the "When" step, where the group-size words are.
    return i.canEnquire
      ? out("when", `Next: groups this size book by enquiry — enquire above, or choose fewer ${i.noun.other}`, "enquire")
      : out("howMany", `Next: choose fewer ${i.noun.other} — groups this size book by enquiry`, "party", fewer);
  }

  // 3. When
  if (!i.dayShort) return out("when", "Next: pick a day", "days");
  if (i.checking) return out("when", "Checking availability…", "days", null, false);
  if (i.startHm == null) {
    if (i.startsOffered) return out("when", `Next: pick a start time for ${i.dayShort}`, "starts");
    if (i.together === "together") {
      const verb = togetherVerb(i.noun);
      return out("when", `Next: ${verb} together on one ${word.one}, or pick another day`, "together");
    }
    if (i.together === "apart") return out("when", `Next: keep one ${word.one}, or pick another day`, "together");
    return out("when", i.dayMessage ?? `Next: pick another day — nothing is free on ${i.dayShort}`, "days");
  }
  if (i.alreadyIn)
    return out("when", `Already in your visit at ${i.startHm} — pick another start time`, "starts");

  return {
    ready: true,
    text: null,
    announce: false,
    step: null,
    target: null,
    unitId: null,
    adjust: null,
    steps: { what: "done", howMany: "done", when: "done" },
  };
}

/**
 * A start pick that makes Add ready (owner, 25 Sept: "When a time is chosen
 * and the booking button is not on screen, scroll to it"): what the panel's
 * polite live region says — keyboard focus stays on the start, so this is
 * how a screen-reader user hears that Add is ready.
 */
export function readyAnnouncement(dayShort: string, startHm: string): string {
  return `Ready to add: ${dayShort} at ${startHm}.`;
}

type Box = { width: number; height: number };

/**
 * Whether that pick brings Add into view, and how. Only when Add is ready
 * and NOT already wholly on screen: `box` is Add's size, `visible` the part
 * of it inside the viewport (an IntersectionObserver's intersectionRect —
 * inside an iframe, the TOP page's viewport), null when none is. One pixel
 * of slack, for fractional layouts. Smooth, or at once under
 * prefers-reduced-motion; "nearest", so the page moves only as far as it
 * must. The panel asks once per pick, never on a re-render.
 */
export function addRevealPlan(i: {
  ready: boolean;
  box: Box;
  visible: Box | null;
  reducedMotion: boolean;
}): { block: "nearest"; behavior: "smooth" | "auto" } | null {
  if (!i.ready) return null;
  if (i.box.width <= 0 || i.box.height <= 0) return null;
  const whole =
    !!i.visible && i.visible.width >= i.box.width - 1 && i.visible.height >= i.box.height - 1;
  if (whole) return null;
  return { block: "nearest", behavior: i.reducedMotion ? "auto" : "smooth" };
}

/** What a step heading says to a screen reader after its label (the tick and the number are hidden). */
export function stepStateSpoken(state: StepState): string {
  return state === "done" ? " (done)" : state === "next" ? " (next step)" : "";
}
